import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import './AuthPage.css';

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<'register' | 'verify'>('register');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    phone: '',
    taothaoUsage: '',
    zaloName: '',
  });
  const [showTaothaoModal, setShowTaothaoModal] = useState(false);
  const [tempTaothaoUsage, setTempTaothaoUsage] = useState('');
  const [tempZaloName, setTempZaloName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsMismatch'));
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError(t('auth.passwordLengthError') || 'Mật khẩu phải dài hơn 6 ký tự');
      return;
    }

    setLoading(true);

    try {
      const result = await authService.register(
        formData.username,
        formData.email,
        formData.password,
        formData.fullName,
        formData.phone,
        formData.taothaoUsage,
        formData.zaloName
      );

      // TẠM THỜI: Nếu skip_verification = true, chuyển thẳng đến trang chính
      if (result.skip_verification) {
        navigate('/community');
      } else {
        // Nếu cần xác thực email (flow cũ)
        setStep('verify');
      }
    } catch (err: any) {
      const errorData = err.response?.data;
      // Handle rate limiting error (429)
      if (err.response?.status === 429) {
        const retryAfter = errorData?.retry_after;
        let errorMsg = errorData?.error;

        // Translate error key if present
        if (errorMsg && (errorMsg.startsWith('AUTH_') || errorMsg === 'AUTH_RATE_LIMIT')) {
          const translated = t(`auth.errors.${errorMsg}`);
          // If translation exists (doesn't return key path), use it
          if (translated !== `auth.errors.${errorMsg}`) {
            errorMsg = translated;
          } else {
            errorMsg = t('common.tooManyRequests');
          }
        } else {
          errorMsg = errorMsg || t('common.tooManyRequests');
        }

        if (retryAfter) {
          const minutes = Math.floor(retryAfter / 60);
          const seconds = retryAfter % 60;
          let timeMsg = '';
          if (minutes > 0) {
            timeMsg = seconds > 0 ? `${minutes} ${t('common.minutes')} ${seconds} ${t('common.seconds')}` : `${minutes} ${t('common.minutes')}`;
          } else {
            timeMsg = `${seconds} ${t('common.seconds')}`;
          }
          setError(`${errorMsg}\n\n⏰ ${t('common.pleaseWait')} ${timeMsg}.`);
        } else {
          setError(errorMsg);
        }
      } else {
        // Handle standard errors
        const errorKey = errorData?.error;
        let errorMessage = errorKey;

        if (errorKey && typeof errorKey === 'string' && errorKey.startsWith('AUTH_')) {
          const translated = t(`auth.errors.${errorKey}`);
          if (translated !== `auth.errors.${errorKey}`) {
            errorMessage = translated;
          }
        }

        setError(errorMessage || t('auth.fail'));
      }

      // Scroll to error message
      setTimeout(() => {
        const errorElement = document.querySelector('.error-message');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.verifyEmail(formData.email, verificationCode);
      navigate('/community');
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.fail'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setResendLoading(true);
    try {
      await authService.resendVerification(formData.email);
      setError('');
      alert(t('auth.codeSent'));
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.fail'));
    } finally {
      setResendLoading(false);
    }
  };

  if (step === 'verify') {
    return (
      <div className="auth-page">
        <div className="auth-wrapper">
          {/* Illustration Side */}
          <div className="auth-illustration">
            <div className="auth-illustration-content">
              <div className="auth-illustration-icon">
                <img src="/images/logo.png" alt="TaoThaoAI Logo" />
              </div>
              <h1>{t('auth.verifyTitle')}</h1>
              <p>
                {t('auth.codeSentTo')} <strong>{formData.email}</strong>
              </p>
            </div>
          </div>

          {/* Form Side */}
          <div className="auth-container">
            <div className="auth-header">
              <h2>{t('auth.verifyEmail')}</h2>
              <p>{t('auth.enterCodeDesc')}</p>
            </div>

            <form onSubmit={handleVerify}>
              <div className="form-group">
                <label htmlFor="verificationCode">{t('auth.enterCode')}</label>
                <div className={`input-wrapper ${verificationCode ? 'has-value' : ''}`}>
                  <span className="input-icon">🔐</span>
                  <input
                    type="text"
                    id="verificationCode"
                    value={verificationCode}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    required
                    style={{ textAlign: 'center', letterSpacing: '8px', fontSize: '1.5rem', fontWeight: 'bold' }}
                  />
                </div>
                <p style={{ fontSize: '0.85rem', color: '#718096', marginTop: '8px', textAlign: 'center' }}>
                  {t('auth.codeSentTo')} <strong>{formData.email}</strong>
                </p>
                <p style={{ fontSize: '0.8rem', color: '#a0aec0', marginTop: '4px', textAlign: 'center' }}>
                  {t('auth.emailHint')}
                </p>
              </div>

              {error && <div className="error-message">{error}</div>}

              <button type="submit" className="submit-button" disabled={loading || verificationCode.length !== 6}>
                {loading ? t('auth.verifying') : t('auth.verifyButton')}
              </button>
            </form>

            <div className="auth-footer">
              <p>
                {t('common.notReceived') || 'Không nhận được?'} {' '}
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendLoading}
                  className="auth-footer-link"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: resendLoading ? 'not-allowed' : 'pointer',
                    padding: 0,
                  }}
                >
                  {resendLoading ? t('auth.sending') : t('auth.resendCode')}
                </button>
              </p>
              <p style={{ marginTop: '16px' }}>
                <button
                  type="button"
                  onClick={() => setStep('register')}
                  className="auth-footer-link"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  ← {t('auth.backToRegister')}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-wrapper">
        {/* Illustration Side */}
        <div className="auth-illustration">
          <div className="auth-illustration-content">
            <div className="auth-illustration-icon">
              <img src="/images/logo.png" alt="TaoThaoAI Logo" />
            </div>
            <h1>{t('auth.startJourney')}</h1>
            <p>
              {t('auth.registerSubtitle')}
            </p>
          </div>
        </div>

        {/* Form Side */}
        <div className="auth-container">
          <div className="auth-header">
            <h2>{t('auth.registerTitle')}</h2>
            <p>{t('auth.registerDesc')}</p>
          </div>

          <form onSubmit={handleRegister}>
            <div className="form-group">
              <label htmlFor="fullName">{t('auth.fullName')}</label>
              <div className={`input-wrapper ${formData.fullName ? 'has-value' : ''}`}>
                <span className="input-icon">👨‍💼</span>
                <input
                  type="text"
                  id="fullName"
                  value={formData.fullName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, fullName: e.target.value })}
                  placeholder={t('auth.fullNamePlaceholder')}
                  required
                  title={t('common.validation.required')}
                  onInvalid={(e) => (e.target as HTMLInputElement).setCustomValidity(t('common.validation.required'))}
                  onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="username">{t('auth.username')}</label>
              <div className={`input-wrapper ${formData.username ? 'has-value' : ''}`}>
                <span className="input-icon">👤</span>
                <input
                  type="text"
                  id="username"
                  value={formData.username}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, username: e.target.value })}
                  placeholder={t('auth.usernameRegisterPlaceholder')}
                  required
                  title={t('common.validation.required')}
                  onInvalid={(e) => (e.target as HTMLInputElement).setCustomValidity(t('common.validation.required'))}
                  onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">{t('auth.email')}</label>
              <div className={`input-wrapper ${formData.email ? 'has-value' : ''}`}>
                <span className="input-icon">📧</span>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  title={t('common.validation.required')}
                  onInvalid={(e) => (e.target as HTMLInputElement).setCustomValidity(t('common.validation.required'))}
                  onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
                />
              </div>
              <p style={{ fontSize: '0.85rem', color: '#718096', marginTop: '6px' }}>
                {t('auth.emailHint')}
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="phone">{t('auth.phone')}</label>
              <div className={`input-wrapper ${formData.phone ? 'has-value' : ''}`}>
                <span className="input-icon">📱</span>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                  placeholder={t('auth.phonePlaceholder')}
                />
              </div>
            </div>

            <div className="form-group taothao-usage-group">
              <label>{t('auth.usedTool')}</label>
              <p style={{ fontSize: '0.85rem', color: '#718096', marginTop: '4px' }}>
                {t('auth.answerBenefits')} 🎁
              </p>
              <button
                type="button"
                className="taothao-open-modal"
                onClick={() => {
                  setTempTaothaoUsage(formData.taothaoUsage || '');
                  setTempZaloName(formData.zaloName || '');
                  setShowTaothaoModal(true);
                }}
              >
                {formData.taothaoUsage
                  ? `${t('common.selected')}${formData.taothaoUsage === 'veo3.1'
                    ? 'Veo3.1 Auto Video Creator'
                    : formData.taothaoUsage === 'whisk'
                      ? 'Whisk Image Creator'
                      : formData.taothaoUsage === 'combo'
                        ? 'Combo All In One'
                        : t('common.neverUsed')
                  }${formData.zaloName ? ` • ${t('common.zaloLabel')}${formData.zaloName}` : ''}`
                  : t('common.clickToSelect')}
              </button>
            </div>

            <div className="form-group">
              <label htmlFor="password">{t('auth.passwordLabel')}</label>
              <div className={`input-wrapper ${formData.password ? 'has-value' : ''}`}>
                <span className="input-icon">🔒</span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={formData.password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={t('auth.createPassword')}
                  required
                  minLength={6}
                  title={t('auth.passwordHint')}
                  onInvalid={(e) => {
                    const target = e.target as HTMLInputElement;
                    if (target.validity.valueMissing) {
                      target.setCustomValidity(t('common.validation.required'));
                    } else if (target.validity.tooShort) {
                      target.setCustomValidity(t('common.validation.minLength', { count: target.value.length }));
                    }
                  }}
                  onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#a0aec0',
                    fontSize: '1.2rem',
                    zIndex: 1,
                  }}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              <p style={{ fontSize: '0.85rem', color: '#718096', marginTop: '6px' }}>
                {t('auth.passwordHint')}
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
              <div className={`input-wrapper ${formData.confirmPassword ? 'has-value' : ''}`}>
                <span className="input-icon">🔒</span>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder={t('auth.confirmPasswordPlaceholder')}
                  required
                  minLength={6}
                  title={t('auth.confirmPasswordPlaceholder')}
                  onInvalid={(e) => {
                    const target = e.target as HTMLInputElement;
                    if (target.validity.valueMissing) {
                      target.setCustomValidity(t('common.validation.required'));
                    } else if (target.validity.tooShort) {
                      target.setCustomValidity(t('common.validation.minLength', { count: target.value.length }));
                    }
                  }}
                  onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#a0aec0',
                    fontSize: '1.2rem',
                    zIndex: 1,
                  }}
                >
                  {showConfirmPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p style={{ fontSize: '0.85rem', color: '#ef4444', marginTop: '6px' }}>
                  ⚠️ {t('auth.passwordsMismatch')}
                </p>
              )}
              {formData.confirmPassword && formData.password === formData.confirmPassword && (
                <p style={{ fontSize: '0.85rem', color: '#10b981', marginTop: '6px' }}>
                  ✅ {t('auth.passwordsMatch')}
                </p>
              )}
            </div>

            {error && (
              <div className="error-message">
                {error}
                {error.includes('Email này đã được sử dụng') && (
                  <div style={{ marginTop: '12px' }}>
                    <Link
                      to="/login"
                      className="auth-footer-link"
                      style={{
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        textDecoration: 'underline'
                      }}
                    >
                      → {t('auth.loginButton')}
                    </Link>
                  </div>
                )}
              </div>
            )}

            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? t('auth.registering') : t('auth.registerButton')}
            </button>
          </form>

          {showTaothaoModal && (
            <div className="taothao-modal-backdrop" onClick={() => setShowTaothaoModal(false)}>
              <div
                className="taothao-modal"
                onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                  e.stopPropagation();
                }}
              >
                <h3 className="taothao-modal-title">{t('auth.usedTool')}</h3>
                <p className="taothao-modal-subtitle">{t('auth.answerBenefits')} 🎁</p>

                <div className="taothao-usage-options">
                  <label className="taothao-usage-option">
                    <input
                      type="radio"
                      name="taothaoUsageModal"
                      value="veo3.1"
                      checked={tempTaothaoUsage === 'veo3.1'}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTempTaothaoUsage(e.target.value)}
                    />
                    <span className="taothao-usage-label">1. Veo3.1 Auto Video Creator</span>
                  </label>
                  <label className="taothao-usage-option">
                    <input
                      type="radio"
                      name="taothaoUsageModal"
                      value="whisk"
                      checked={tempTaothaoUsage === 'whisk'}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTempTaothaoUsage(e.target.value)}
                    />
                    <span className="taothao-usage-label">2. Whisk Image Creator</span>
                  </label>
                  <label className="taothao-usage-option">
                    <input
                      type="radio"
                      name="taothaoUsageModal"
                      value="combo"
                      checked={tempTaothaoUsage === 'combo'}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTempTaothaoUsage(e.target.value)}
                    />
                    <span className="taothao-usage-label">3. Combo All In One</span>
                  </label>
                  <label className="taothao-usage-option taothao-usage-never">
                    <span className="taothao-usage-never-main">
                      <input
                        type="radio"
                        name="taothaoUsageModal"
                        value="never"
                        checked={tempTaothaoUsage === 'never'}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTempTaothaoUsage(e.target.value)}
                      />
                      <span className="taothao-usage-label">4. {t('common.neverUsed')}</span>
                    </span>
                  </label>
                </div>

                <div className="taothao-zalo-input-wrapper" style={{ marginTop: '10px' }}>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '4px',
                      fontSize: '0.85rem',
                      color: '#4a5568',
                      fontWeight: 500,
                    }}
                  >
                    {t('common.zaloName')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('common.zaloPlaceholder')}
                    value={tempZaloName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTempZaloName(e.target.value)}
                    className="taothao-zalo-input"
                  />
                </div>

                <div className="taothao-modal-actions">
                  <button
                    type="button"
                    className="taothao-modal-btn taothao-modal-btn-secondary"
                    onClick={() => setShowTaothaoModal(false)}
                  >
                    {t('common.later')}
                  </button>
                  <button
                    type="button"
                    className="taothao-modal-btn taothao-modal-btn-primary"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        taothaoUsage: tempTaothaoUsage,
                        zaloName: tempZaloName,
                      });
                      setShowTaothaoModal(false);
                    }}
                  >
                    {t('common.save')}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="auth-footer">
            <p>
              {t('auth.haveAccount')}{' '}
              <Link to="/login" className="auth-footer-link">
                {t('auth.loginButton')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
